import { atom, selector } from "recoil";
import {
  Parameter,
  ParameterSpace,
  Slice,
  Vector3
} from "./parameterAtoms";

export const zeroVectorState = atom<Vector3>({
  key: 'zeroVectorState',
  default: { x: 0, y: 0, z: 0 },
});

export const infVectorState = atom<Vector3>({
  key: 'infVectorState',
  default: { x: 99999, y: 99999, z: 99999 },
});

// ユーザが入力したParameterを一時的に保管する変数 //
export const stageParameterState = atom<Parameter>({
  key: 'stageParameterState',
  default: {
    name: '',
    controlPoints: { x: 0, y: 0, z: 0 },
    morphingMin: { x: 0, y: 0, z: 0 },
    morphingMax: { x: 0, y: 0, z: 0 },
    ySymmetry: false,
    targetControlPointsX: { start: 0, stop: 0 },
    targetControlPointsY: { start: 0, stop: 0 },
    targetControlPointsZ: { start: 0, stop: 0 },
    morphingDirection: 'x',
    morphingParameter: { lower: 0, upper: 0 },
  }
});

// `name`プロパティに対するRecoilState
export const parameterNameState = selector<string>({
  key: 'parameterNameState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.name;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      name: newValue as string, // nameだけを更新
    });
  },
});

// `controlPoints`プロパティに対するRecoilState
export const controlPointsState = selector<Vector3>({
  key: 'controlPointsState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.controlPoints;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      controlPoints: newValue as Vector3, // controlPointsだけを更新
    });
  },
});

// `morphingMin`プロパティに対するRecoilState
export const morphingMinState = selector<Vector3>({
  key: 'morphingMinState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.morphingMin;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      morphingMin: newValue as Vector3, // morphingMinだけを更新
    });
  },
});

// `morphingMax`プロパティに対するRecoilState
export const morphingMaxState = selector<Vector3>({
  key: 'morphingMaxState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.morphingMax;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      morphingMax: newValue as Vector3, // morphingMaxだけを更新
    });
  },
});

// `ySymmetry`プロパティに対するRecoilState
export const ySymmetryState = selector<boolean>({
  key: 'ySymmetryState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.ySymmetry;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      ySymmetry: newValue as boolean, // ySymmetryだけを更新
    });
  },
});

// `targetControlPointsXState`プロパティに対するRecoilState
export const targetControlPointsXState = selector<Slice>({
  key: 'targetControlPointsXState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.targetControlPointsX;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      targetControlPointsX: newValue as Slice, // targetControlPointsXだけを更新
    });
  },
});

// `targetControlPointsYState`プロパティに対するRecoilState
export const targetControlPointsYState = selector<Slice>({
  key: 'targetControlPointsYState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.targetControlPointsY;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      targetControlPointsY: newValue as Slice, // targetControlPointsYだけを更新
    });
  },
});

// `targetControlPointsZState`プロパティに対するRecoilState
export const targetControlPointsZState = selector<Slice>({
  key: 'targetControlPointsZState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.targetControlPointsZ;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      targetControlPointsZ: newValue as Slice, // targetControlPointsZだけを更新
    });
  },
});

// `morphingDirection`プロパティに対するRecoilState
export const morphingDirectionState = selector<string>({
  key: 'morphingDirectionState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.morphingDirection;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      morphingDirection: newValue as string, // morphingDirectionだけを更新
    });
  },
});

// `morphingParameter`プロパティに対するRecoilState
export const morphingParameterState = selector<ParameterSpace>({
  key: 'morphingParameterState',
  get: ({ get }) => {
    const stageParameter = get(stageParameterState);
    return stageParameter.morphingParameter;
  },
  set: ({ get, set }, newValue) => {
    const stageParameter = get(stageParameterState);
    set(stageParameterState, {
      ...stageParameter,
      morphingParameter: newValue as ParameterSpace, // morphingParameterだけを更新
    });
  },
});

// ユーザがドロップダウンで選択したconfig nameを一時保管する変数 //
export const selectedParameterNameState = atom<string>({
  key: 'selectedParameterNameState',
  default: ''
});

// ユーザが入力したconfig file nameを一時保管する変数 //
export const fileNameState = atom<string>({
  key: 'fileNameState',
  default: 'settings.yml'
});

// y-面対象が使用できるかフラグ
export const SymmetryDisableState = atom<boolean>({
  key: 'SymmetryDisableState',
  default: true
});